import React, { useEffect, useState } from 'react';

import { SimpleTooltip } from '@eluve/components';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Heading,
  Icon,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { AppointmentFileTypesLookup } from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';
import { usePrevious } from '@eluve/utility-hooks';
import {
  isAudioFileChunkFile,
  recordingTimestampFromFileName,
} from '@eluve/utils';

import {
  useIsUploadInProgressForAppointment,
  usePeriodicRemoteFileSyncActor,
} from './hooks/usePeriodicRemoteFileSyncMachine';
import { FileRow, SWAP_FILE_EXT } from './opfs';

export interface FilesActionCellProps {
  appointmentId: string;
  appointmentDir: FileSystemDirectoryHandle;
  files: FileRow[];
  onLocalFilesUpdated: (id: string) => Promise<void>;
}

export const FilesActionCell: React.FC<FilesActionCellProps> = ({
  appointmentId,
  files,
  appointmentDir,
  onLocalFilesUpdated,
}) => {
  const toast = useToast();
  const logger = useNamedLogger('appointment-files');
  const analytics = useAnalytics('files');

  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { send: sendToPeriodicRemoteFileSyncMachine } =
    usePeriodicRemoteFileSyncActor();

  const hasPendingAppointmentTask =
    useIsUploadInProgressForAppointment(appointmentId);

  const isSessionInProgress = files.some((f) => f.name.endsWith(SWAP_FILE_EXT));

  const wasPreviouslyUploading = usePrevious(hasPendingAppointmentTask);

  useEffect(() => {
    if (wasPreviouslyUploading && !hasPendingAppointmentTask) {
      onLocalFilesUpdated(appointmentId);
    }
  }, [
    hasPendingAppointmentTask,
    wasPreviouslyUploading,
    appointmentId,
    onLocalFilesUpdated,
  ]);

  const handleDeleteFiles = async () => {
    const fileNames = files.map((f) => f.name);
    try {
      analytics.capture('delete_attempt', {
        appointmentId,
        fileCount: files.length,
        fileNames,
      });
      logger.info('Deleting files for appointment', {
        appointmentId,
        numFiles: files.length,
      });
      setIsDeleting(true);
      const deletePromises = files.map(async ({ name }) =>
        appointmentDir.removeEntry(name),
      );

      await Promise.all(deletePromises);
      analytics.capture('delete_success', {
        appointmentId,
        fileCount: files.length,
        fileNames,
      });
      logger.info('Files deleted successfully', {
        appointmentId,
        fileCount: files.length,
      });
      toast.success('Files deleted successfully');
      setIsConfirmDeleteDialogOpen(false);
      await onLocalFilesUpdated(appointmentId);
    } catch (e) {
      analytics.capture('delete_failed', {
        appointmentId,
        fileCount: files.length,
        fileNames,
      });
      logger.error(`Failed to delete files.`, {
        appointmentId,
        errorMessage: (e as { message?: string })?.message,
      });
      toast.error('Failed to delete files');
    } finally {
      setIsDeleting(false);
    }
  };

  const attemptReuploadFiles = () => {
    analytics.capture('reupload_attempt', {
      appointmentId,
      fileCount: files.length,
      fileNames: files.map((f) => f.name),
    });
    logger.info('Reuploading files for appointment', {
      appointmentId,
      numFiles: files.length,
    });

    for (const file of files) {
      // Remove the file extension here to get the recording started at
      // in order to correlate the segment on the backend
      const recordingStartedAt = recordingTimestampFromFileName(file.name);
      logger.info('Reuploading file for appointment', {
        appointmentId,
        numFiles: files.length,
        fileName: file.name,
        recordingStartedAt,
      });

      sendToPeriodicRemoteFileSyncMachine({
        type: 'IMMEDIATE_SYNC_AVAILABLE',
        file: {
          appointmentId,
          attemptCount: 0,
          id: file.name,
          lastUploadAttemptedAt: new Date().toISOString(),
          shouldBeTranscribed: true,
          uploadType: isAudioFileChunkFile(file.name)
            ? AppointmentFileTypesLookup.CHUNKED_AUDIO
            : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
          // TODO(jesse)[ELU-1684]:
          // We don't really know if the session was degraded or not.
          // If we stored info in something like IndexedDB we could reference it here
          wasDegraded: false,
        },
      });
    }
  };

  return (
    <>
      <Stack direction="horizontal" fullWidth={false} gap={1}>
        <SimpleTooltip label="Try and re-upload files">
          <Button
            size="sm"
            variant="outline"
            onClick={attemptReuploadFiles}
            disabled={hasPendingAppointmentTask || isSessionInProgress}
          >
            <Icon name="CloudUpload" size="lg" />
          </Button>
        </SimpleTooltip>
        <SimpleTooltip label="Delete files">
          <Button
            disabled={isSessionInProgress}
            size="sm"
            variant="destructive"
            onClick={() => setIsConfirmDeleteDialogOpen(true)}
          >
            <Icon name="Trash" size="lg" />
          </Button>
        </SimpleTooltip>
      </Stack>
      <Dialog
        open={isConfirmDeleteDialogOpen}
        onOpenChange={setIsConfirmDeleteDialogOpen}
      >
        <DialogContent size="lg">
          <Heading size="3xl">Are you sure?</Heading>
          <Text size="xs">
            If these files are removed but haven't been synced to our backend,
            data may be lost.
          </Text>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsConfirmDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={handleDeleteFiles}
            >
              Yes, I'm sure.
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
